import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Upload, File, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const DocumentUpload = ({ dealId, onUploadSuccess, showDocType = true }) => {
  const [file, setFile] = useState(null);
  const [docType, setDocType] = useState('lender_term_sheet');
  const [uploading, setUploading] = useState(false);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    try {
      const { documentsAPI } = await import('../services/api');
      await documentsAPI.upload(dealId, docType, 'admin', file);
      toast.success('Document uploaded successfully!');
      setFile(null);
      if (onUploadSuccess) onUploadSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card className="glass-effect border-slate-200 dark:border-slate-700 hover-lift">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <div className="w-10 h-10 gradient-primary rounded-lg flex items-center justify-center">
            <Upload className="w-5 h-5 text-white" />
          </div>
          Upload Document
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {showDocType && (
          <div className="space-y-2">
            <Label>Document Type</Label>
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="lender_term_sheet">Lender Term Sheet</SelectItem>
                <SelectItem value="purchase_agreement">Purchase Agreement</SelectItem>
                <SelectItem value="rehab_budget">Rehab Budget</SelectItem>
                <SelectItem value="bank_statement">Bank Statement</SelectItem>
                <SelectItem value="borrower_engagement">Borrower Engagement</SelectItem>
                <SelectItem value="borrower_fee_agreement">Fee Agreement</SelectItem>
                <SelectItem value="lsp_agreement">LSP Agreement</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="space-y-2">
          <Label>Select File</Label>
          <div className="relative">
            <Input
              type="file"
              onChange={handleFileChange}
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              className="cursor-pointer"
            />
          </div>
          {file && (
            <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span className="text-sm text-green-700 dark:text-green-400">{file.name}</span>
              <span className="text-xs text-green-600 dark:text-green-500 ml-auto">
                {(file.size / 1024).toFixed(1)} KB
              </span>
            </div>
          )}
        </div>

        <Button
          onClick={handleUpload}
          disabled={!file || uploading}
          className="w-full gradient-primary border-0 hover-scale"
        >
          {uploading ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              Uploading...
            </>
          ) : (
            <>
              <Upload className="w-4 h-4 mr-2" />
              Upload Document
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};

export default DocumentUpload;